import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { useNavigate } from 'react-router-dom';
import {
  Wand2,
  X,
  Loader2,
  Plus,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Pencil,
  Check,
} from 'lucide-react';
import supabase from '../supabase';
import EditQuestionsChat, { type QuestionEdit } from './edit-questions-chat';
import CreateLevel from './create-level';
import { useWorkspace } from '../context/workspace-context';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { useToast } from './ui/toast';
import { Sentry } from '../lib/sentry';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { useCourse, type Question } from '../hooks/use-course';
import type {
  StoredAnalysis,
  StoredLevel,
  StoredStructure,
} from '../interfaces/course-analysis';
import type { Organization } from '../interfaces/organization';
import type { ProposedLevel } from '../interfaces/proposed-level';

interface GeneratedQuestion {
  question: string;
  type: string;
  options?: string[];
  correctAnswer: string | number | number[];
  explanation?: string;
  difficulty?: string;
}

function readOptions(question: Question): string[] {
  return Array.isArray(question.options) ? question.options : [];
}

function readAnswerIndexes(question: Question): number | number[] {
  const answer = question.correct_answer;
  if (typeof answer !== 'string') return answer;
  try {
    return JSON.parse(answer);
  } catch {
    return 0;
  }
}

function shiftAnswerAfterRemoval(
  answer: number | number[],
  removedIndex: number,
): number | number[] {
  if (Array.isArray(answer)) {
    return answer
      .filter((index) => index !== removedIndex)
      .map((index) => (index > removedIndex ? index - 1 : index));
  }
  if (answer === removedIndex) return 0;
  if (answer > removedIndex) return answer - 1;
  return answer;
}

export default function EditCourse({ courseId }: { courseId: number }) {
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();

  const {
    data: courseData,
    isLoading: isCourseLoading,
    refetch: refetchCourse,
  } = useCourse(courseId, currentWorkspace?.id);
  const course = courseData?.course ?? null;
  const loading = isCourseLoading || isLoadingWorkspaces;

  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showAIEditor, setShowAIEditor] = useState(false);
  const [editingLevelId, setEditingLevelId] = useState<number | null>(null);
  const [focusedQuestionId, setFocusedQuestionId] = useState<
    string | undefined
  >(undefined);
  const navigate = useNavigate();
  const [generatingLevelId, setGeneratingLevelId] = useState<number | null>(
    null,
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [courseAnalysis, setCourseAnalysis] = useState<StoredAnalysis | null>(
    null,
  );
  const [organizationData, setOrganizationData] = useState<Organization | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccessMessage, setIsSuccessMessage] = useState(false);
  const [analysisStructureIssue, setAnalysisStructureIssue] = useState<
    string | null
  >(null);
  // New state to track AI edit processing
  const [isApplyingAIEdits, setIsApplyingAIEdits] = useState(false);
  // Track analysis update operation
  const [isUpdatingAnalysis, setIsUpdatingAnalysis] = useState(false);

  // New state variables for level management
  const [showAddLevelDialog, setShowAddLevelDialog] = useState(false);
  const [deletingLevelId, setDeletingLevelId] = useState<number | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // State for renaming a level
  const [renamingLevelId, setRenamingLevelId] = useState<number | null>(null);
  const [renameLevelName, setRenameLevelName] = useState('');

  // New state variables for question deletion
  const [deletingQuestionId, setDeletingQuestionId] = useState<number | null>(
    null,
  );
  const [showDeleteQuestionConfirmation, setShowDeleteQuestionConfirmation] =
    useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    if (!currentWorkspace || isLoadingWorkspaces) return;
    fetchCourseAnalysis();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, currentWorkspace, isLoadingWorkspaces]);

  /**
   * Validates and attempts to repair the analysis structure if it's malformed
   */
  const validateAndRepairAnalysis = (
    analysis: StoredAnalysis | null,
  ): StoredAnalysis | null => {
    if (!analysis) {
      setAnalysisStructureIssue('Analysis data is missing');
      return null;
    }

    let modified = false;
    const repairedAnalysis: StoredAnalysis = { ...analysis };
    const issueDetails: string[] = [];

    // Ensure recommendedStructure exists
    if (!repairedAnalysis.recommendedStructure) {
      repairedAnalysis.recommendedStructure = {};
      modified = true;
      issueDetails.push('Missing recommended structure');
    }

    // Ensure levels exists and is an array
    if (!repairedAnalysis.recommendedStructure.levels) {
      repairedAnalysis.recommendedStructure.levels = [];
      modified = true;
      issueDetails.push('Missing levels array');
    } else if (!Array.isArray(repairedAnalysis.recommendedStructure.levels)) {
      const levelsObject = repairedAnalysis.recommendedStructure.levels;
      // Try to convert object to array if it's an object with numeric keys
      if (typeof levelsObject === 'object') {
        const levelsArray = Object.keys(levelsObject)
          .filter((key) => !isNaN(Number(key)))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => levelsObject[key]);

        if (levelsArray.length > 0) {
          repairedAnalysis.recommendedStructure.levels = levelsArray;
          modified = true;
          issueDetails.push('Levels was not properly formatted as an array');
        } else {
          repairedAnalysis.recommendedStructure.levels = [];
          modified = true;
          issueDetails.push('Levels could not be parsed from object');
        }
      } else {
        repairedAnalysis.recommendedStructure.levels = [];
        modified = true;
        issueDetails.push('Levels was not an array or object');
      }
    }

    if (modified) {
      setAnalysisStructureIssue(
        `Course analysis has structural issues: ${issueDetails.join(
          ', ',
        )}. Attempts were made to repair it, but generation may fail.`,
      );
    } else {
      setAnalysisStructureIssue(null);
    }

    return repairedAnalysis;
  };

  /**
   * Normalizes the analysis structure to ensure it has the expected format
   * Handles common issues like nested analysis objects
   */
  const normalizeAnalysisStructure = (
    analysisData: StoredAnalysis | null,
  ): StoredAnalysis | null => {
    if (!analysisData) return null;

    let normalizedAnalysis: StoredAnalysis = { ...analysisData };

    // Case 1: Check for nested analysis structure (analysis.analysis)
    if (
      !normalizedAnalysis.recommendedStructure &&
      normalizedAnalysis.analysis &&
      typeof normalizedAnalysis.analysis === 'object'
    ) {
      // Extract the nested structure
      normalizedAnalysis = normalizedAnalysis.analysis;
    }

    // Case 2: Handle stringified JSON
    if (
      !normalizedAnalysis.recommendedStructure &&
      typeof normalizedAnalysis === 'string'
    ) {
      try {
        const parsedAnalysis = JSON.parse(normalizedAnalysis);
        if (parsedAnalysis && typeof parsedAnalysis === 'object') {
          normalizedAnalysis = parsedAnalysis;

          // Check for nested structure in parsed object
          if (
            !normalizedAnalysis.recommendedStructure &&
            normalizedAnalysis.analysis &&
            typeof normalizedAnalysis.analysis === 'object'
          ) {
            normalizedAnalysis = normalizedAnalysis.analysis;
          }
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    return normalizedAnalysis;
  };

  /**
   * Fetches course analysis data needed for question generation
   */
  const fetchCourseAnalysis = async () => {
    try {
      // Fetch course analysis
      const { data: analysisData, error: analysisError } = await supabase
        .from('course_analysis')
        .select('analysis')
        .eq('course_id', courseId)
        .single();

      if (analysisError) {
        setErrorMessage('Failed to fetch course analysis data.');
        return;
      }

      if (!analysisData || !analysisData.analysis) {
        setErrorMessage('No course analysis data found.');
        return;
      }

      // Check if this is a nested structure issue (analysis.analysis pattern)
      if (
        !analysisData.analysis.recommendedStructure &&
        analysisData.analysis.analysis &&
        typeof analysisData.analysis.analysis === 'object'
      ) {
        // Check if the real structure is one level deeper
        if (analysisData.analysis.analysis.recommendedStructure) {
          // Use the nested structure instead
          analysisData.analysis = analysisData.analysis.analysis;
        }
      }

      // Normalize the analysis structure
      const normalizedAnalysis = normalizeAnalysisStructure(
        analysisData.analysis,
      );
      if (normalizedAnalysis) {
        // Validate the normalized structure
        const validatedAnalysis = validateAndRepairAnalysis(normalizedAnalysis);
        if (validatedAnalysis) {
          setCourseAnalysis(validatedAnalysis);
        } else {
          setErrorMessage(
            'Course analysis structure is invalid and could not be repaired.',
          );
          return;
        }
      }

      // Fetch organization data from current workspace
      if (currentWorkspace) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('id, name, about, country, industry')
          .eq('id', currentWorkspace.id)
          .single();

        if (!orgError && orgData) {
          setOrganizationData(orgData);
        }
      }
    } catch (error) {
      setErrorMessage('An error occurred while fetching course analysis data.');
      Sentry.captureException(error);
    }
  };

  /**
   * Updates a question in the database
   */
  const handleSaveQuestion = async (question: Question) => {
    try {
      const { error } = await supabase
        .from('questions')
        .update({
          content: question.content,
          type: question.type,
          options: question.options,
          correct_answer: question.correct_answer,
          explanation: question.explanation,
        })
        .eq('id', question.id);

      if (error) throw error;

      setEditingQuestion(null);
      refetchCourse(); // Refresh the questions
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  /**
   * Generates questions for a specific level
   */
  const handleGenerateQuestions = async (
    levelId: number,
    levelNumber: number,
  ) => {
    if (!courseAnalysis) {
      showErrorMessage(
        'Missing course analysis. Please ensure course analysis is completed first.',
      );
      return;
    }

    if (!courseAnalysis.recommendedStructure) {
      // Check if we have a nested structure issue
      if (
        courseAnalysis.analysis &&
        courseAnalysis.analysis.recommendedStructure
      ) {
        setCourseAnalysis(courseAnalysis.analysis);
        showErrorMessage(
          'Analysis structure was fixed. Please try generating questions again.',
        );
      } else {
        showErrorMessage(
          'Course analysis format is invalid. Missing recommended structure.',
        );
      }
      return;
    }

    if (
      !courseAnalysis.recommendedStructure.levels ||
      !Array.isArray(courseAnalysis.recommendedStructure.levels)
    ) {
      showErrorMessage(
        'Course analysis format is invalid. Missing levels configuration.',
      );
      return;
    }

    if (
      levelNumber < 1 ||
      levelNumber > courseAnalysis.recommendedStructure.levels.length
    ) {
      // Calculate the current available levels range
      const availableLevels = courseAnalysis.recommendedStructure.levels.length;
      const actualLevels = course?.levels?.length || 0;

      // If we have more actual levels than in the analysis, suggest updating the analysis
      if (actualLevels > availableLevels) {
        showErrorMessage(
          `Level ${levelNumber} is out of range. Available levels in the analysis: 1-${availableLevels}. ` +
            `You have ${actualLevels} actual levels. Please click "Update Analysis" to sync the analysis with your current levels.`,
        );
      } else {
        showErrorMessage(
          `Level ${levelNumber} is out of range. Available levels: 1-${courseAnalysis.recommendedStructure.levels.length}`,
        );
      }
      return;
    }

    setGeneratingLevelId(levelId);
    setErrorMessage(null);

    try {
      // Find the level configuration in the course analysis
      const levelConfig =
        courseAnalysis.recommendedStructure.levels[levelNumber - 1];

      // Double-check we have the level config (should be redundant with checks above but safer)
      if (!levelConfig) {
        throw new Error(`Level ${levelNumber} configuration not found`);
      }

      // Fetch course details for API request
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select(
          `
          id,
          name,
          description,
          purpose,
          subject_matter
        `,
        )
        .eq('id', courseId)
        .single();

      if (courseError) {
        throw courseError;
      }

      // Get course content if available
      const { data: contentData } = await supabase
        .from('course_content')
        .select('content')
        .eq('course_id', courseId)
        .single();

      // Get previously generated questions to maintain consistency
      const { data: previousQuestions } = await supabase
        .from('questions')
        .select('*')
        .eq('course_id', courseId)
        .order('level_id', { ascending: true })
        .order('id', { ascending: true });

      // Get required questions for this level
      const { data: levelData } = await supabase
        .from('course_levels')
        .select('required_questions')
        .eq('id', levelId)
        .single();

      const requiredQuestions = levelData?.required_questions || [];

      // Make API request to generate questions
      const response = await apiFetch(ENDPOINTS.QUESTION_GENERATION, {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          analysis: courseAnalysis,
          levelConfig: {
            ...levelConfig,
            level: levelNumber - 1, // API expects 0-indexed level
          },
          previousQuestions: previousQuestions || [],
          courseDetails: {
            name: courseData.name,
            purpose: courseData.purpose,
            subjectMatter: courseData.subject_matter,
            description: courseData.description,
          },
          organizationDetails: organizationData,
          courseContent: contentData?.content || null,
          requiredQuestions: requiredQuestions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate questions');
      }

      const data = await response.json();

      // Store conversationId for subsequent calls
      if (data.conversationId) {
        setConversationId(data.conversationId);
      }

      if (!data.questions || !Array.isArray(data.questions)) {
        throw new Error('Invalid question generation response format');
      }

      // Format questions for database insertion
      const questionsToInsert = data.questions.map((q: GeneratedQuestion) => {
        const options = q.options || [];
        let correctAnswer;

        // Format correct answer based on question type
        if (q.type === 'singleSelect') {
          correctAnswer =
            typeof q.correctAnswer === 'number'
              ? q.correctAnswer
              : parseInt(String(q.correctAnswer));
        } else if (q.type === 'multipleSelect') {
          correctAnswer = Array.isArray(q.correctAnswer)
            ? q.correctAnswer
            : [parseInt(String(q.correctAnswer))];
        } else {
          // Fill in blank
          correctAnswer = q.correctAnswer;
        }

        return {
          course_id: courseId,
          level_id: levelId,
          content: q.question,
          type: q.type,
          options: JSON.stringify(options),
          correct_answer:
            typeof correctAnswer === 'string'
              ? correctAnswer
              : JSON.stringify(correctAnswer),
          explanation: q.explanation || '',
          difficulty: q.difficulty || levelConfig.difficulty,
        };
      });

      const { error: insertError } = await supabase
        .from('questions')
        .insert(questionsToInsert);

      if (insertError) {
        Sentry.captureException(insertError);
        throw new Error(insertError.message);
      }

      // Refresh questions list
      refetchCourse();

      showSuccessMessage(
        `Successfully generated questions for Level ${levelNumber}!`,
      );
    } catch (error) {
      showErrorMessage(
        error instanceof Error
          ? `Failed to generate questions: ${error.message}`
          : 'Failed to generate questions. Please try again.',
      );
    } finally {
      setGeneratingLevelId(null);
    }
  };

  const handleEditsReceived = async (edits: QuestionEdit[]) => {
    // Set loading state to true when we start applying edits
    setIsApplyingAIEdits(true);

    try {
      // Show a message that edits are being applied
      showSuccessMessage('Applying AI edits to questions...', false);

      // Process each edit immediately
      for (const edit of edits) {
        const { questionId, field, newValue } = edit;
        const dbField = mapFieldToDatabase(field);

        // Format value based on field type
        let formattedValue = newValue;
        if (field === 'correctAnswer') {
          // Handle non-JSON strings in correctAnswer
          try {
            // If it's already a number or array, stringify it
            if (typeof newValue === 'number' || Array.isArray(newValue)) {
              formattedValue = JSON.stringify(newValue);
            } else if (typeof newValue === 'string') {
              // Try to parse it as JSON first
              try {
                JSON.parse(newValue);
                formattedValue = newValue; // If it's valid JSON, use as is
              } catch {
                // If it's not valid JSON, treat it as a fill-in-blank answer
                formattedValue = JSON.stringify(newValue);
              }
            }
          } catch (error) {
            continue;
          }
        } else if (field === 'options') {
          // Ensure options is an array before stringifying
          const optionsArray = Array.isArray(newValue) ? newValue : [];
          formattedValue = JSON.stringify(optionsArray);
        }

        // Ensure questionId is a number
        const parsedQuestionId = parseInt(questionId);
        if (isNaN(parsedQuestionId)) {
          continue;
        }

        const { error } = await supabase
          .from('questions')
          .update({ [dbField]: formattedValue })
          .eq('id', parsedQuestionId)
          .eq('course_id', courseId)
          .select();

        if (error) {
          throw error;
        }
      }

      // Refresh the questions after all updates
      await refetchCourse();

      // Show success message
      showSuccessMessage('AI edits have been successfully applied!', true);
    } catch (error) {
      showErrorMessage('Error applying AI edits. Please try again.');
    } finally {
      // Set loading state back to false
      setIsApplyingAIEdits(false);
    }
  };

  const mapFieldToDatabase = (field: string): string => {
    const fieldMap: Record<string, string> = {
      question: 'content',
      correctAnswer: 'correct_answer',
      options: 'options',
      explanation: 'explanation',
      difficulty: 'difficulty',
      type: 'type',
    };
    return fieldMap[field] || field;
  };

  const handleAIEdit = (levelId: number, questionId?: string) => {
    setEditingLevelId(levelId);
    setFocusedQuestionId(questionId);
    setShowAIEditor(true);
  };

  // Add this new component for editing options and correct answers
  const QuestionOptionsEdit = ({
    type,
    options,
    correctAnswer,
    onChange,
  }: {
    type: string;
    options: string[];
    correctAnswer: number | number[];
    onChange: (value: number | number[]) => void;
  }) => {
    if (type === 'singleSelect') {
      return (
        <RadioGroup
          value={
            Array.isArray(correctAnswer)
              ? correctAnswer[0].toString()
              : correctAnswer.toString()
          }
          onValueChange={(value) => onChange(parseInt(value))}
          className="space-y-2"
        >
          {options.map((option, index) => (
            <div key={index} className="flex items-center space-x-2">
              <RadioGroupItem
                value={index.toString()}
                id={`edit-option-${index}`}
              />
              <Label htmlFor={`edit-option-${index}`}>{option}</Label>
            </div>
          ))}
        </RadioGroup>
      );
    }

    if (type === 'multipleSelect') {
      const correctAnswers = Array.isArray(correctAnswer)
        ? correctAnswer
        : [correctAnswer];

      return (
        <div className="space-y-2">
          {options.map((option, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Checkbox
                id={`edit-option-${index}`}
                checked={correctAnswers.includes(index)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onChange([...correctAnswers, index].sort((a, b) => a - b));
                  } else {
                    onChange(correctAnswers.filter((i) => i !== index));
                  }
                }}
              />
              <Label htmlFor={`edit-option-${index}`}>{option}</Label>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Add this new component for displaying options with correct answers
  const QuestionOptionsDisplay = ({
    type,
    options,
    correctAnswer,
  }: {
    type: string;
    options: string[];
    correctAnswer: number | number[];
  }) => {
    const correctAnswers = Array.isArray(correctAnswer)
      ? correctAnswer
      : [correctAnswer];

    if (type === 'singleSelect') {
      return (
        <RadioGroup value={correctAnswers[0].toString()} className="space-y-2">
          {options.map((option, index) => (
            <div key={index} className="flex items-center space-x-2">
              <RadioGroupItem
                value={index.toString()}
                id={`option-${index}`}
                disabled
                checked={correctAnswers.includes(index)}
              />
              <Label
                htmlFor={`option-${index}`}
                className={correctAnswers.includes(index) ? 'font-medium' : ''}
              >
                {option}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );
    }

    if (type === 'multipleSelect') {
      return (
        <div className="space-y-2">
          {options.map((option, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Checkbox
                id={`option-${index}`}
                disabled
                checked={correctAnswers.includes(index)}
              />
              <Label
                htmlFor={`option-${index}`}
                className={correctAnswers.includes(index) ? 'font-medium' : ''}
              >
                {option}
              </Label>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Add a function to navigate back to analysis page
  const navigateToAnalysisPage = () => {
    navigate(`/courses/analysis/${courseId}`);
  };

  // Add a debug function to expose current analysis state
  const debugAnalysisStructure = () => {
    if (!courseAnalysis) {
      alert('No analysis data available');
      return;
    }

    const debugInfo = {
      hasRecommendedStructure: !!courseAnalysis.recommendedStructure,
      topLevelKeys: Object.keys(courseAnalysis),
      hasNestedAnalysis: !!courseAnalysis.analysis,
      hasLevels: !!courseAnalysis.recommendedStructure?.levels,
      levelsIsArray: Array.isArray(courseAnalysis.recommendedStructure?.levels),
      levelsCount: courseAnalysis.recommendedStructure?.levels?.length || 0,
    };

    alert(JSON.stringify(debugInfo, null, 2));
  };

  /**
   * Creates a new level in the database
   */
  const handleAddLevel = async (levelData: ProposedLevel) => {
    try {
      // Calculate the next level number
      let nextLevelNumber = 1;
      if (course?.levels && course.levels.length > 0) {
        nextLevelNumber = Math.max(...course.levels.map((l) => l.level)) + 1;
      }

      // Prepare the level data for insertion
      // Remove "Level X:" prefix from the name if it exists
      let cleanedName = levelData.name || `Level ${nextLevelNumber}`;
      if (cleanedName.match(/^Level \d+:/)) {
        cleanedName = cleanedName.replace(/^Level \d+:\s*/, '');
      }

      const newLevel = {
        course_id: courseId,
        level: nextLevelNumber,
        name: cleanedName,
        description: levelData.description || '',
        difficulty: levelData.difficulty || 'beginner',
        topics: levelData.topics || [],
        question_count: levelData.totalQuestions || 0,
        single_select_count: levelData.questionDistribution?.singleSelect || 0,
        multiple_select_count:
          levelData.questionDistribution?.multipleSelect || 0,
        fill_in_blank_count: levelData.questionDistribution?.fillInBlank || 0,
        required_questions: levelData.required_questions || [],
      };

      // Insert the new level into the database
      const { data, error } = await supabase
        .from('course_levels')
        .insert(newLevel)
        .select();

      if (error) throw error;

      // Close the dialog and refresh the course data
      setShowAddLevelDialog(false);
      await refetchCourse();

      // Automatically update the analysis structure
      await handleUpdateAnalysisStructure();

      return data;
    } catch (error) {
      showErrorMessage('Failed to add level. Please try again.');
      return null;
    }
  };

  /**
   * Manually updates the course analysis structure to match current levels
   */
  const handleUpdateAnalysisStructure = async () => {
    if (!course || !course.levels || course.levels.length === 0) {
      showErrorMessage(
        'No course levels available to update analysis structure.',
      );
      return;
    }

    try {
      setIsUpdatingAnalysis(true);

      // Check if we have a valid course analysis structure to update
      if (!courseAnalysis || !courseAnalysis.recommendedStructure) {
        showErrorMessage(
          'No valid course analysis structure to update. Please run course analysis first.',
        );
        return;
      }

      // Create an updated analysis structure with current levels
      const structure: StoredStructure = courseAnalysis.recommendedStructure;
      const updatedAnalysis: StoredAnalysis = {
        ...courseAnalysis,
        recommendedStructure: structure,
      };

      // If the levels array doesn't exist or isn't an array, initialize it
      const levels: StoredLevel[] = Array.isArray(structure.levels)
        ? structure.levels
        : [];
      structure.levels = levels;

      // Get the current number of levels in the analysis
      const currentLevelCount = levels.length;
      const actualLevelCount = course.levels.length;

      // If we have more actual levels than in the analysis, add placeholders
      if (actualLevelCount > currentLevelCount) {
        for (let i = currentLevelCount; i < actualLevelCount; i++) {
          const level = course.levels[i];
          // Create a new level entry based on the actual level data
          levels.push({
            name: level.name || `Level ${level.level}`,
            description: level.description || '',
            difficulty: level.difficulty || 'beginner',
            topics: level.topics || [],
            questionCount: 10, // Default value
            questionDistribution: {
              singleSelect: 5,
              multipleSelect: 3,
              fillInBlank: 2,
            },
          });
        }
      }

      // Update the total levels count
      structure.totalLevels = levels.length;

      // Recalculate the total questions
      structure.totalQuestions = levels.reduce(
        (sum, level) => sum + (level.questionCount || 10),
        0,
      );

      // Update state with the new analysis structure
      setCourseAnalysis(updatedAnalysis);
      setAnalysisStructureIssue(null);
      setErrorMessage(null);

      // Save the updated analysis back to the database
      const { data: existingAnalysis, error: fetchError } = await supabase
        .from('course_analysis')
        .select('id')
        .eq('course_id', courseId)
        .maybeSingle();

      if (fetchError) {
        throw new Error('Failed to check for existing analysis');
      }

      if (existingAnalysis) {
        // Update existing analysis record - removed updated_at field
        const { error: updateError } = await supabase
          .from('course_analysis')
          .update({
            analysis: updatedAnalysis,
          })
          .eq('id', existingAnalysis.id);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Insert new analysis record - removed created_at and updated_at fields
        const { error: insertError } = await supabase
          .from('course_analysis')
          .insert({
            course_id: courseId,
            analysis: updatedAnalysis,
          });

        if (insertError) {
          throw insertError;
        }
      }

      // Show success message
      showSuccessMessage(
        'Course analysis has been successfully updated to match current levels.',
      );
    } catch (error) {
      showErrorMessage(
        'Failed to update analysis structure. Please try again.',
      );
    } finally {
      setIsUpdatingAnalysis(false);
    }
  };

  /**
   * Deletes a level from the database
   */
  const startRenameLevel = (level: {
    id: number;
    level: number;
    name: string;
  }) => {
    const prefix = `Level ${level.level}:`;
    const nameWithoutPrefix = level.name.startsWith(prefix)
      ? level.name.substring(prefix.length).trim()
      : level.name;

    setRenamingLevelId(level.id);
    setRenameLevelName(nameWithoutPrefix);
  };

  const handleRenameLevel = async () => {
    if (!renamingLevelId) return;

    const newName = renameLevelName.trim();
    if (!newName) return;

    try {
      const { error } = await supabase
        .from('course_levels')
        .update({ name: newName })
        .eq('id', renamingLevelId);

      if (error) throw error;

      await refetchCourse();
    } catch (error) {
      setErrorMessage('Failed to rename level. Please try again.');
    } finally {
      setRenamingLevelId(null);
      setRenameLevelName('');
    }
  };

  const handleDeleteLevel = async () => {
    if (!deletingLevelId) return;

    try {
      // First, delete all questions associated with this level
      const { error: questionsError } = await supabase
        .from('questions')
        .delete()
        .eq('level_id', deletingLevelId);

      if (questionsError) throw questionsError;

      // Then delete the level itself
      const { error: levelError } = await supabase
        .from('course_levels')
        .delete()
        .eq('id', deletingLevelId);

      if (levelError) throw levelError;

      // Refresh the course data
      await refetchCourse();

      // Update any level numbers that need to be adjusted
      if (course?.levels) {
        const deletedLevel = course.levels.find(
          (l) => l.id === deletingLevelId,
        );
        if (deletedLevel) {
          const deletedLevelNumber = deletedLevel.level;

          // Get all levels with higher numbers than the deleted one
          const higherLevels = course.levels
            .filter((l) => l.level > deletedLevelNumber)
            .sort((a, b) => a.level - b.level);

          // Update their level numbers and fix their names
          for (const level of higherLevels) {
            // Clean the name to remove any existing "Level X:" prefix
            let cleanedName = level.name;
            if (cleanedName.match(/^Level \d+:/)) {
              cleanedName = cleanedName.replace(/^Level \d+:\s*/, '');
            }

            await supabase
              .from('course_levels')
              .update({
                level: level.level - 1,
                name: cleanedName, // Store the cleaned name
              })
              .eq('id', level.id);
          }

          // Refresh again to get the updated data
          await refetchCourse();
        }
      }
    } catch (error) {
      setErrorMessage('Failed to delete level. Please try again.');
    } finally {
      setShowDeleteConfirmation(false);
      setDeletingLevelId(null);
    }
  };

  /**
   * Deletes a question from the database
   */
  const handleDeleteQuestion = async () => {
    if (!deletingQuestionId) return;

    try {
      // Get the level_id of the question before deleting it
      const { data: questionData, error: questionError } = await supabase
        .from('questions')
        .select('level_id')
        .eq('id', deletingQuestionId)
        .single();

      if (questionError) throw questionError;

      const levelId = questionData.level_id;

      // Delete the question from the database
      const { error } = await supabase
        .from('questions')
        .delete()
        .eq('id', deletingQuestionId);

      if (error) throw error;

      // Get the updated count of questions for this level
      const { data: remainingQuestions, error: countError } = await supabase
        .from('questions')
        .select('id, type')
        .eq('level_id', levelId);

      if (countError) throw countError;

      // Calculate counts by question type
      const totalCount = remainingQuestions?.length || 0;
      const singleSelectCount =
        remainingQuestions?.filter((q) => q.type === 'singleSelect').length ||
        0;
      const multipleSelectCount =
        remainingQuestions?.filter((q) => q.type === 'multipleSelect').length ||
        0;
      const fillInBlankCount =
        remainingQuestions?.filter((q) => q.type === 'fillInBlank').length || 0;

      // Update the level's question_count field
      const { error: updateError } = await supabase
        .from('course_levels')
        .update({
          question_count: totalCount,
          single_select_count: singleSelectCount,
          multiple_select_count: multipleSelectCount,
          fill_in_blank_count: fillInBlankCount,
        })
        .eq('id', levelId);

      if (updateError) throw updateError;

      // Refresh the questions list
      await refetchCourse();

      // Show success toast notification instead of the message at the top
      showToast('Question deleted successfully', 'success');
    } catch (error) {
      showToast('Failed to delete question. Please try again.', 'error');
    } finally {
      setShowDeleteQuestionConfirmation(false);
      setDeletingQuestionId(null);
    }
  };

  // Helper to set error message
  const showErrorMessage = (message: string) => {
    setIsSuccessMessage(false);
    setErrorMessage(message);
  };

  // Helper to set success message
  const showSuccessMessage = (message: string, autoDismiss = true) => {
    setIsSuccessMessage(true);
    setErrorMessage(message);

    if (autoDismiss) {
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!course) {
    return <div className="p-4">Failed to load course details</div>;
  }

  const editingLevel = course.levels.find((l) => l.id === editingLevelId);

  return (
    <div
      className={`grid ${
        showAIEditor ? 'grid-cols-[1fr,400px]' : 'grid-cols-1'
      } gap-4 transition-all duration-300`}
    >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{course?.name} - Questions Editor</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-1"
              onClick={handleUpdateAnalysisStructure}
              disabled={loading || isUpdatingAnalysis}
            >
              <RefreshCw
                className={`h-4 w-4 ${isUpdatingAnalysis ? 'animate-spin' : ''}`}
              />
              Update Analysis
            </Button>
            <Button
              variant="outline"
              className="gap-1"
              onClick={() => setShowAddLevelDialog(true)}
            >
              <Plus className="h-4 w-4" /> Add Level
            </Button>
          </div>
        </CardHeader>
        <CardContent className="relative">
          {/* Loading overlay for AI edits */}
          {isApplyingAIEdits && (
            <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex flex-col items-center justify-center z-50">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
              <p className="text-lg font-medium">Applying AI Edits...</p>
              <p className="text-sm text-muted-foreground mt-2">
                The changes are being processed and saved.
              </p>
            </div>
          )}

          {analysisStructureIssue && (
            <div className="mb-6 p-4 border border-yellow-300 bg-yellow-50 rounded-md">
              <h3 className="text-yellow-800 font-medium mb-2">
                Course Analysis Issue Detected
              </h3>
              <p className="text-yellow-700 mb-4">{analysisStructureIssue}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={navigateToAnalysisPage}
                  className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800"
                >
                  Return to Analysis Page
                </Button>
                <Button
                  variant="outline"
                  onClick={handleUpdateAnalysisStructure}
                  className="bg-green-100 hover:bg-green-200 text-green-800"
                >
                  Update Analysis Structure
                </Button>
                <Button
                  variant="outline"
                  onClick={debugAnalysisStructure}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800"
                >
                  Debug Analysis Structure
                </Button>
              </div>
            </div>
          )}

          {errorMessage && (
            <div
              className={`mb-6 p-4 border rounded-md ${
                isSuccessMessage
                  ? 'border-green-300 bg-green-50'
                  : 'border-red-300 bg-red-50'
              }`}
            >
              <p
                className={isSuccessMessage ? 'text-green-700' : 'text-red-700'}
              >
                {errorMessage}
              </p>
              <Button
                variant="outline"
                onClick={() => setErrorMessage(null)}
                className="mt-2"
              >
                Dismiss
              </Button>
            </div>
          )}

          <Accordion type="single" collapsible className="space-y-4">
            {course?.levels?.map((level) => (
              <AccordionItem key={level.id} value={`level-${level.id}`}>
                <div className="flex items-center justify-between">
                  {renamingLevelId === level.id ? (
                    <div className="flex flex-grow items-center gap-2 py-4">
                      <span className="text-lg font-semibold whitespace-nowrap">
                        Level {level.level}:
                      </span>
                      <Input
                        value={renameLevelName}
                        onChange={(e) => setRenameLevelName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameLevel();
                          if (e.key === 'Escape') setRenamingLevelId(null);
                        }}
                        className="max-w-md"
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRenameLevel}
                        disabled={!renameLevelName.trim()}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setRenamingLevelId(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <AccordionTrigger className="text-lg font-semibold flex-grow">
                        Level {level.level}:{' '}
                        {level.name.startsWith(`Level ${level.level}:`)
                          ? level.name
                              .substring(`Level ${level.level}:`.length)
                              .trim()
                          : level.name}
                      </AccordionTrigger>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startRenameLevel(level)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {renamingLevelId !== level.id && (
                    <div className="flex gap-2 mr-4">
                      {level.questions.length > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAIEdit(level.id);
                          }}
                          disabled={isApplyingAIEdits}
                        >
                          <Wand2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingLevelId(level.id);
                          setShowDeleteConfirmation(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <AccordionContent>
                  <div className="space-y-4">
                    {level.questions.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground mb-4">
                          No questions have been generated for this level yet.
                        </p>
                        <Button
                          onClick={() =>
                            handleGenerateQuestions(level.id, level.level)
                          }
                          className="gap-2"
                          disabled={generatingLevelId === level.id}
                        >
                          {generatingLevelId === level.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Generating Questions...
                            </>
                          ) : (
                            <>
                              <Wand2 className="h-4 w-4" />
                              Generate Questions
                            </>
                          )}
                        </Button>
                        {errorMessage && generatingLevelId === null && (
                          <p className="mt-4 text-red-500 text-sm">
                            {errorMessage}
                          </p>
                        )}
                      </div>
                    ) : (
                      level.questions.map((question) => (
                        <Card key={question.id} className="p-4">
                          {editingQuestion?.id === question.id ? (
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium">
                                  Question Content
                                </label>
                                <Textarea
                                  value={editingQuestion.content}
                                  onChange={(e) =>
                                    setEditingQuestion({
                                      ...editingQuestion,
                                      content: e.target.value,
                                    })
                                  }
                                  className="mt-1"
                                />
                              </div>

                              <div>
                                <label className="text-sm font-medium">
                                  Question Type
                                </label>
                                <Select
                                  value={editingQuestion.type}
                                  onValueChange={(value) => {
                                    const oldType = editingQuestion.type;
                                    const isOldFillInBlank =
                                      oldType === 'fillInBlank';
                                    const isNewSelectType =
                                      value === 'singleSelect' ||
                                      value === 'multipleSelect';
                                    const isNewFillInBlank =
                                      value === 'fillInBlank';

                                    if (isOldFillInBlank && isNewSelectType) {
                                      const oldAnswer = String(
                                        editingQuestion.correct_answer || '',
                                      );
                                      setEditingQuestion({
                                        ...editingQuestion,
                                        type: value,
                                        options: oldAnswer ? [oldAnswer] : [''],
                                        correct_answer: JSON.stringify(0),
                                      });
                                    } else if (
                                      !isOldFillInBlank &&
                                      isNewFillInBlank
                                    ) {
                                      let correctIndex = 0;
                                      try {
                                        const parsed =
                                          typeof editingQuestion.correct_answer ===
                                          'string'
                                            ? JSON.parse(
                                                editingQuestion.correct_answer,
                                              )
                                            : editingQuestion.correct_answer;
                                        correctIndex = Array.isArray(parsed)
                                          ? parsed[0]
                                          : parsed;
                                      } catch {
                                        correctIndex = 0;
                                      }
                                      const options = Array.isArray(
                                        editingQuestion.options,
                                      )
                                        ? editingQuestion.options
                                        : [];
                                      const answerText =
                                        options[correctIndex] || '';
                                      setEditingQuestion({
                                        ...editingQuestion,
                                        type: value,
                                        options: [],
                                        correct_answer: answerText,
                                      });
                                    } else {
                                      setEditingQuestion({
                                        ...editingQuestion,
                                        type: value,
                                      });
                                    }
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="singleSelect">
                                      Single Select
                                    </SelectItem>
                                    <SelectItem value="multipleSelect">
                                      Multiple Select
                                    </SelectItem>
                                    <SelectItem value="fillInBlank">
                                      Fill in the Blank
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              {(editingQuestion.type === 'singleSelect' ||
                                editingQuestion.type === 'multipleSelect') && (
                                <div>
                                  <label className="text-sm text-muted-foreground">
                                    Options and Correct Answer
                                  </label>
                                  <div className="space-y-4 mt-2">
                                    <div>
                                      <label className="text-sm text-muted-foreground">
                                        Options
                                      </label>
                                      <div className="space-y-2 mt-2">
                                        {Array.isArray(
                                          editingQuestion.options,
                                        ) &&
                                          editingQuestion.options.map(
                                            (option, index) => (
                                              <div
                                                key={index}
                                                className="flex gap-2"
                                              >
                                                <Input
                                                  value={option}
                                                  onChange={(e) => {
                                                    const newOptions = [
                                                      ...readOptions(
                                                        editingQuestion,
                                                      ),
                                                    ];
                                                    newOptions[index] =
                                                      e.target.value;
                                                    setEditingQuestion({
                                                      ...editingQuestion,
                                                      options: newOptions,
                                                    });
                                                  }}
                                                  placeholder={`Option ${
                                                    index + 1
                                                  }`}
                                                  className="flex-1"
                                                />
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  onClick={() => {
                                                    const newAnswer =
                                                      shiftAnswerAfterRemoval(
                                                        readAnswerIndexes(
                                                          editingQuestion,
                                                        ),
                                                        index,
                                                      );
                                                    setEditingQuestion({
                                                      ...editingQuestion,
                                                      options: readOptions(
                                                        editingQuestion,
                                                      ).filter(
                                                        (_, i) => i !== index,
                                                      ),
                                                      correct_answer:
                                                        JSON.stringify(
                                                          newAnswer,
                                                        ),
                                                    });
                                                  }}
                                                >
                                                  <X className="h-4 w-4" />
                                                </Button>
                                              </div>
                                            ),
                                          )}
                                        <Button
                                          variant="outline"
                                          className="w-full"
                                          onClick={() => {
                                            const newOptions = Array.isArray(
                                              editingQuestion.options,
                                            )
                                              ? [...editingQuestion.options, '']
                                              : [''];
                                            setEditingQuestion({
                                              ...editingQuestion,
                                              options: newOptions,
                                            });
                                          }}
                                        >
                                          Add Option
                                        </Button>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-sm text-muted-foreground mt-4 block">
                                        Select Correct Answer
                                        {editingQuestion.type ===
                                        'multipleSelect'
                                          ? '(s)'
                                          : ''}
                                      </label>
                                      <div className="mt-2">
                                        <QuestionOptionsEdit
                                          type={editingQuestion.type}
                                          options={
                                            Array.isArray(
                                              editingQuestion.options,
                                            )
                                              ? editingQuestion.options
                                              : []
                                          }
                                          correctAnswer={(() => {
                                            if (
                                              typeof editingQuestion.correct_answer ===
                                              'string'
                                            ) {
                                              try {
                                                return JSON.parse(
                                                  editingQuestion.correct_answer,
                                                );
                                              } catch {
                                                return 0;
                                              }
                                            }
                                            return editingQuestion.correct_answer;
                                          })()}
                                          onChange={(value) =>
                                            setEditingQuestion({
                                              ...editingQuestion,
                                              correct_answer:
                                                JSON.stringify(value),
                                            })
                                          }
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {editingQuestion.type === 'fillInBlank' && (
                                <div>
                                  <label className="text-sm font-medium">
                                    Correct Answer
                                  </label>
                                  <Input
                                    value={String(
                                      editingQuestion.correct_answer ?? '',
                                    )}
                                    onChange={(e) =>
                                      setEditingQuestion({
                                        ...editingQuestion,
                                        correct_answer: e.target.value,
                                      })
                                    }
                                    className="mt-1"
                                  />
                                </div>
                              )}

                              <div>
                                <label className="text-sm font-medium">
                                  Explanation
                                </label>
                                <Textarea
                                  value={editingQuestion.explanation || ''}
                                  onChange={(e) =>
                                    setEditingQuestion({
                                      ...editingQuestion,
                                      explanation: e.target.value,
                                    })
                                  }
                                  className="mt-1"
                                />
                              </div>

                              <div className="flex gap-2">
                                <Button
                                  onClick={() =>
                                    handleSaveQuestion(editingQuestion)
                                  }
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => setEditingQuestion(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <h3 className="font-medium mb-2">
                                {question.content}
                              </h3>
                              <div className="text-sm text-gray-500 space-y-3">
                                <p>Type: {question.type}</p>
                                {(question.type === 'singleSelect' ||
                                  question.type === 'multipleSelect') && (
                                  <div className="mt-2">
                                    <p className="mb-2 text-sm font-medium">
                                      Options (✓ = Correct Answer):
                                    </p>
                                    <QuestionOptionsDisplay
                                      type={question.type}
                                      options={
                                        Array.isArray(question.options)
                                          ? question.options
                                          : []
                                      }
                                      correctAnswer={(() => {
                                        if (
                                          typeof question.correct_answer ===
                                          'string'
                                        ) {
                                          try {
                                            return JSON.parse(
                                              question.correct_answer,
                                            );
                                          } catch {
                                            return 0;
                                          }
                                        }
                                        return question.correct_answer;
                                      })()}
                                    />
                                  </div>
                                )}
                                {question.type === 'fillInBlank' && (
                                  <p>
                                    Correct Answer: {question.correct_answer}
                                  </p>
                                )}
                                {question.explanation && (
                                  <p className="mt-2">
                                    Explanation: {question.explanation}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2 mt-4">
                                <Button
                                  variant="outline"
                                  onClick={() => setEditingQuestion(question)}
                                >
                                  Edit Question
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-100"
                                  onClick={() => {
                                    setDeletingQuestionId(question.id);
                                    setShowDeleteQuestionConfirmation(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleAIEdit(
                                      level.id,
                                      question.id.toString(),
                                    )
                                  }
                                  disabled={isApplyingAIEdits}
                                >
                                  <Wand2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </Card>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Add level action button at the bottom */}
          <div className="mt-8 flex justify-center">
            <Button
              onClick={() => setShowAddLevelDialog(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add New Level
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Question Editor Sidebar */}
      {showAIEditor && editingLevel && (
        <div className="fixed right-0 top-0 bottom-0 w-[400px] bg-background border-l shadow-lg transition-transform duration-300 transform translate-x-0">
          <div className="h-full flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">
                  Edit Level {editingLevel.level} Questions
                </h3>
                {isApplyingAIEdits && (
                  <div className="flex items-center gap-1 text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Applying edits...</span>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowAIEditor(false);
                  setEditingLevelId(null);
                  setFocusedQuestionId(undefined);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-grow overflow-auto">
              <EditQuestionsChat
                levelId={editingLevel.id.toString()}
                questions={editingLevel.questions.map((q) => ({
                  id: q.id.toString(),
                  type: q.type as
                    | 'singleSelect'
                    | 'multipleSelect'
                    | 'fillInBlank',
                  question: q.content,
                  options: q.options,
                  correctAnswer: (() => {
                    if (typeof q.correct_answer !== 'string')
                      return q.correct_answer;
                    try {
                      return JSON.parse(q.correct_answer);
                    } catch {
                      return q.correct_answer;
                    }
                  })(),
                  level: editingLevel.level,
                  explanation: q.explanation,
                  difficulty: q.difficulty as
                    | 'beginner'
                    | 'intermediate'
                    | 'advanced',
                }))}
                levelInfo={{
                  level: editingLevel.level,
                  name: editingLevel.name,
                  description: editingLevel.description,
                }}
                focusedQuestionId={focusedQuestionId}
                onEditsReceived={handleEditsReceived}
                onClose={() => {
                  setShowAIEditor(false);
                  setEditingLevelId(null);
                  setFocusedQuestionId(undefined);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Level Dialog */}
      <Dialog open={showAddLevelDialog} onOpenChange={setShowAddLevelDialog}>
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Create New Level</DialogTitle>
            <DialogDescription>
              Chat with AI to create a new level for this course
            </DialogDescription>
          </DialogHeader>

          <div className="flex-grow overflow-hidden">
            <CreateLevel
              courseId={courseId}
              existingLevels={course?.levels || []}
              onClose={() => setShowAddLevelDialog(false)}
              onLevelCreated={handleAddLevel}
              hideHeader={true}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Level Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirm Level Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this level? This will permanently
              remove the level and all of its questions.
              {course?.levels && deletingLevelId && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md">
                  <p className="font-medium">
                    Level{' '}
                    {course.levels.find((l) => l.id === deletingLevelId)?.level}
                    :{' '}
                    {course.levels.find((l) => l.id === deletingLevelId)?.name}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {
                      course.levels.find((l) => l.id === deletingLevelId)
                        ?.questions.length
                    }{' '}
                    questions will be deleted
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleDeleteLevel}>
              Delete Level
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add delete question confirmation dialog */}
      <Dialog
        open={showDeleteQuestionConfirmation}
        onOpenChange={(open) => {
          if (!open) {
            setShowDeleteQuestionConfirmation(false);
            setDeletingQuestionId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteQuestionConfirmation(false);
                setDeletingQuestionId(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteQuestion}>
              Delete Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
